<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import unflatten from '@/utils/unfletten';
import { getTenantContext } from '@/composables/useTenant';
import Icon from '@/Components/Icon.vue';
import { resolveThemedIconUrl } from '@tenant-registry';
import { toCssColorValue } from '@/utils/sheetLinksWidget';

const tenantSettings = computed(() => getTenantContext().settings);
const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
    showColorSheet: {
        type: Boolean,
        default: false
    },
    sheetColorClass: {
        type: String,
        default: ''
    },
    invert: {
        type: Boolean,
        default: false
    },
    options: {
        type: Array,
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    },
    maxHeight: {
        type: String,
        default: 'auto'
    },
    selected: {
        type: Object,
        default: null
    },
    background: {
        type: String,
        default: 'white'
    },
    classText: {
        type: String,
        default: ''
    },
    classTextChildren: {
        type: String,
        default: ''
    },
    triggerIcon: {
        type: String,
        default: 'chevron'
    },
    colorIcon: {
        type: String,
        default: 'var(--color-chevron-icon)'
    },
    boxShadow: {
        type: String,
        default: ''
    },
    firstBorder: {
        type: Boolean,
        default: false
    },
    principalClass: {
        type: String,
        default: ''
    },
    tooltipWhenTruncated: {
        type: Boolean,
        default: false
    },
    clearable: {
        type: Boolean,
        default: false
    }
});

const emits = defineEmits(['update:modelValue']);

const theme = ref(getTenantContext().theme);
const open = ref(false);
const dropdown = ref(null);
const trigger = ref(null);
const optionsId = `dropdown-options-${useId()}`;
const optionList = ref(null);
const sublevelList = ref(null);
const persistentScrollbarRail = ref(null);
const persistentScrollbarThumb = ref(null);
const persistentScrollbarVisible = ref(false);
const icons = ref({});
const viewportMargin = 16;
const persistentScrollbarInset = 2;
const persistentScrollbarRightInset = 1;
const persistentScrollbarMinThumbHeight = 18;
let optionListResizeObserver = null;
let persistentScrollbarDrag = null;
let persistentScrollbarAnimationFrame = null;
let persistentScrollbarMetrics = {
    maxScrollTop: 0,
    maxThumbOffset: 0,
    thumbHeight: 0,
};

const internalSelected = ref(props.selected || props.options[0] || null);
const dropdownMaxHeight = ref(props.maxHeight);
const sublevelMaxHeight = ref(props.maxHeight);

const selectedOption = computed({
    get() {
        const selected = props.selected ?? internalSelected.value;
        // Locale changes rebuild option labels. Keep the selected identity, but
        // render the current option rather than the label captured at selection.
        const currentOptions = props.options.flatMap(option => [option, ...(option?.sublevels ?? [])]);
        return currentOptions.find(option => isSameOption(option, selected))
            ?? selected ?? props.options[0] ?? null;
    },
    set(option) {
        internalSelected.value = option;
    }
});

const defaultOption = computed(() => props.options[0] ?? null);

const sheetColors = computed(() => {
    const settings = unflatten(tenantSettings.value);
    return settings?.style?.color?.sheet ?? {};
});

const getViewportMaxHeight = (availableHeight) => {
    const clampedHeight = Math.max(availableHeight, 0);

    if (props.maxHeight === 'auto') {
        return `${clampedHeight}px`;
    }

    return `min(${props.maxHeight}, ${clampedHeight}px)`;
};

const updateDropdownMaxHeight = () => {
    if (!dropdown.value) return;

    const dropdownRect = dropdown.value.getBoundingClientRect();
    const availableHeight = window.innerHeight - dropdownRect.bottom - viewportMargin;

    dropdownMaxHeight.value = getViewportMaxHeight(availableHeight);
};

const stopObservingOptionList = () => {
    optionListResizeObserver?.disconnect();
    optionListResizeObserver = null;
};

const updatePersistentScrollbarThumbPosition = () => {
    persistentScrollbarAnimationFrame = null;

    const element = optionList.value;
    const thumb = persistentScrollbarThumb.value;
    const { maxScrollTop, maxThumbOffset } = persistentScrollbarMetrics;

    if (!element || !thumb || maxScrollTop <= 0) {
        return;
    }

    const scrollProgress = Math.min(Math.max(element.scrollTop / maxScrollTop, 0), 1);
    const thumbOffset = persistentScrollbarInset + maxThumbOffset * scrollProgress;

    thumb.style.transform = `translate3d(0, ${thumbOffset}px, 0)`;
};

const queuePersistentScrollbarThumbPosition = () => {
    if (persistentScrollbarAnimationFrame !== null) {
        return;
    }

    persistentScrollbarAnimationFrame = window.requestAnimationFrame(
        updatePersistentScrollbarThumbPosition
    );
};

const syncPersistentScrollbar = () => {
    const element = optionList.value;
    const rail = persistentScrollbarRail.value;
    const thumb = persistentScrollbarThumb.value;

    if (!open.value || !element || !rail || !thumb) {
        return;
    }

    const viewportHeight = element.clientHeight;
    const scrollHeight = element.scrollHeight;
    const maxScrollTop = Math.max(scrollHeight - viewportHeight, 0);
    const canScroll = viewportHeight > 0 && maxScrollTop > 1;

    persistentScrollbarVisible.value = canScroll;
    rail.style.height = `${viewportHeight}px`;

    const optionListRect = element.getBoundingClientRect();
    const dropdownRect = dropdown.value?.getBoundingClientRect();

    if (optionListRect.width > 0 && dropdownRect) {
        rail.style.right = 'auto';
        rail.style.left = `${Math.max(
            optionListRect.right - dropdownRect.left - rail.offsetWidth - persistentScrollbarRightInset,
            0
        )}px`;
    }

    if (!canScroll) {
        persistentScrollbarMetrics = {
            maxScrollTop: 0,
            maxThumbOffset: 0,
            thumbHeight: 0,
        };
        return;
    }

    const availableTrackHeight = Math.max(viewportHeight - persistentScrollbarInset * 2, 0);
    const thumbHeight = Math.min(
        availableTrackHeight,
        Math.max(persistentScrollbarMinThumbHeight, availableTrackHeight * viewportHeight / scrollHeight)
    );
    const maxThumbOffset = Math.max(availableTrackHeight - thumbHeight, 0);

    persistentScrollbarMetrics = { maxScrollTop, maxThumbOffset, thumbHeight };

    thumb.style.height = `${thumbHeight}px`;
    updatePersistentScrollbarThumbPosition();
};

const refreshPersistentScrollbar = async () => {
    stopObservingOptionList();

    if (!open.value) {
        return;
    }

    await nextTick();

    if (!optionList.value) {
        return;
    }

    if (typeof ResizeObserver !== 'undefined') {
        optionListResizeObserver = new ResizeObserver(syncPersistentScrollbar);
        optionListResizeObserver.observe(optionList.value);
    }

    syncPersistentScrollbar();
};

const endPersistentScrollbarDrag = () => {
    if (!persistentScrollbarDrag) {
        return;
    }

    document.removeEventListener('pointermove', handlePersistentScrollbarDrag);
    document.removeEventListener('pointerup', endPersistentScrollbarDrag);
    document.removeEventListener('pointercancel', endPersistentScrollbarDrag);
    persistentScrollbarDrag = null;
};

function handlePersistentScrollbarDrag(event) {
    if (!persistentScrollbarDrag || !optionList.value) {
        return;
    }

    const { startY, startScrollTop, maxScrollTop, maxThumbOffset } = persistentScrollbarDrag;

    if (maxThumbOffset <= 0) {
        return;
    }

    optionList.value.scrollTop = startScrollTop
        + ((event.clientY - startY) / maxThumbOffset) * maxScrollTop;
}

const startPersistentScrollbarDrag = (event) => {
    const element = optionList.value;
    const rail = persistentScrollbarRail.value;
    const thumb = persistentScrollbarThumb.value;

    if (!element || !rail || !thumb || !persistentScrollbarVisible.value) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    persistentScrollbarDrag = {
        startY: event.clientY,
        startScrollTop: element.scrollTop,
        maxScrollTop: persistentScrollbarMetrics.maxScrollTop,
        maxThumbOffset: persistentScrollbarMetrics.maxThumbOffset,
    };

    document.addEventListener('pointermove', handlePersistentScrollbarDrag);
    document.addEventListener('pointerup', endPersistentScrollbarDrag);
    document.addEventListener('pointercancel', endPersistentScrollbarDrag);
};

const handlePersistentScrollbarRailPointerDown = (event) => {
    const element = optionList.value;
    const rail = persistentScrollbarRail.value;
    const thumb = persistentScrollbarThumb.value;

    if (!element || !rail || !thumb || event.target === thumb) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    const railRect = rail.getBoundingClientRect();
    const { maxScrollTop, maxThumbOffset, thumbHeight } = persistentScrollbarMetrics;
    const desiredThumbOffset = Math.min(
        Math.max(event.clientY - railRect.top - thumbHeight / 2 - persistentScrollbarInset, 0),
        maxThumbOffset
    );
    const scrollProgress = maxThumbOffset > 0 ? desiredThumbOffset / maxThumbOffset : 0;

    element.scrollTop = scrollProgress * maxScrollTop;
};

const handlePersistentScrollbarWheel = (event) => {
    if (!optionList.value) {
        return;
    }

    optionList.value.scrollTop += event.deltaY;
};

const getOptionColor = (option) => {
    if (!option) return null;

    if (option.color) return option.color;

    if (option.name && sheetColors.value[option.name]) return sheetColors.value[option.name];

    return null;
};

const getOptionColorStyle = (option) => {
    const color = getOptionColor(option);

    return color
        ? { backgroundColor: toCssColorValue(color, 'transparent') }
        : {};
};

const getOptionText = (option) => {
    if (option === null || option === undefined) return '';

    if (typeof option === 'object') {
        return String(option.complete_label ?? option.label ?? option.name ?? option.value ?? option.id ?? '');
    }

    return String(option);
};

const getOptionTitle = (option) => {
    const text = getOptionText(option);

    return props.tooltipWhenTruncated ? null : text;
};

const getOptionKey = (option) => {
    if (option === null || option === undefined) return '';

    if (typeof option === 'object') {
        return String(option.id ?? option.value ?? option.code ?? option.name ?? option.complete_label ?? option.label ?? '');
    }

    return String(option);
};

const isSameOption = (left, right) => getOptionKey(left) === getOptionKey(right);

const canClearSelection = computed(() => (
    props.clearable
    && !props.disabled
    && defaultOption.value !== null
    && !isSameOption(selectedOption.value, defaultOption.value)
));

const updateTruncatedTitle = (event, option) => {
    if (!props.tooltipWhenTruncated || !event?.currentTarget) return;

    const element = event.currentTarget;
    const text = getOptionText(option);
    element.title = text && element.scrollWidth > element.clientWidth ? text : '';
};

const isGroupOption = (option) => Array.isArray(option?.sublevels) && option.sublevels.length > 0;

const loadThemedIcon = async (iconName, preferredTheme = theme.value) => {
    return resolveThemedIconUrl(iconName, preferredTheme);
};

const getIconOption = async (option) => {
    if (!option.icon) return null;

    if (!(option.icon in icons.value)) {
        if (option.icon.startsWith('http') || option.icon.startsWith('/') || option.icon.startsWith('data:')) {
            icons.value[option.icon] = option.icon;
        } else {
            const iconName = option.icon.endsWith('.svg') ? option.icon.slice(0, -4) : option.icon;
            icons.value[option.icon] = await loadThemedIcon(iconName) ?? '';
        }
    }

    return icons.value[option.icon];
};

const preloadIcons = async (options = []) => {
    const tasks = [];

    for (const option of options) {
        if (option?.icon) {
            tasks.push(getIconOption(option));
        }

        if (tasks.length) {
            await Promise.all(tasks);
        }
    }
};

const focusTrigger = () => nextTick(() => trigger.value?.focus());

function selectOption(option, event) {
    if (props.disabled) return;
    if (isGroupOption(option)) {
        if (event?.type === 'keydown') {
            handleItemHover(option, event);
            nextTick(() => sublevelList.value?.querySelector('li')?.focus());
        }
        return;
    }

    selectedOption.value = option;
    emits('update:modelValue', option);
    open.value = false;
    if (event?.type === 'keydown') focusTrigger();
}

function clearSelection() {
    if (!canClearSelection.value) return;

    selectedOption.value = defaultOption.value;
    emits('update:modelValue', defaultOption.value);
    open.value = false;
}

function closeOnEscape(e) {
    if (!open.value) return;
    if (e.key === 'Escape') {
        const hadFocus = dropdown.value?.contains(document.activeElement);
        open.value = false;
        if (hadFocus) focusTrigger();
    }
}

onClickOutside(dropdown, () => {
    open.value = false;
});

onMounted(async () => {
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updateDropdownMaxHeight);
    document.addEventListener('scroll', updateDropdownMaxHeight, true);

    await preloadIcons([selectedOption.value, ...props.options]);
});

watch(() => props.disabled, disabled => { if (disabled) open.value = false; });

watch(selectedOption, (option) => {
    if (option?.icon) {
        getIconOption(option);
    }
});

watch(open, async (isOpen) => {
    if (!isOpen) {
        handleMouseLeave();
        return;
    }

    persistentScrollbarVisible.value = false;
    await nextTick();
    updateDropdownMaxHeight();
});

watch(() => props.maxHeight, async () => {
    dropdownMaxHeight.value = props.maxHeight;
    sublevelMaxHeight.value = props.maxHeight;

    if (!open.value) return;

    await nextTick();
    updateDropdownMaxHeight();
});

watch(
    () => [open.value, props.options.length, dropdownMaxHeight.value],
    refreshPersistentScrollbar
);

const sublevel = ref([]);
const hoveredOption = ref(null);
const isHover = ref(false);
const sublevelPosition = ref({ top: 0, left: 0, width: 0 });

const sublevelStyles = computed(() => ({
    top: `${sublevelPosition.value.top}px`,
    left: `${sublevelPosition.value.left}px`,
    maxHeight: sublevelMaxHeight.value,
    width: sublevelPosition.value.width
        ? `${sublevelPosition.value.width}px`
        : 'auto'
}));

const borderRound = ref(true);

const handleItemHover = async (option, event) => {
    hoveredOption.value = option;
    const list = option.sublevels ?? [];

    sublevel.value = list;

    if (list.length && event?.currentTarget && dropdown.value) {
        const targetRect = event.currentTarget.getBoundingClientRect();
        const dropdownRect = dropdown.value.getBoundingClientRect();

        const maxHeightNumber = parseInt(props.maxHeight);
        const totalHeight = targetRect.height * props.options.length;

        const height = Number.isNaN(maxHeightNumber)
            ? totalHeight
            : Math.min(totalHeight, maxHeightNumber + targetRect.height);

        const bottomSize = dropdownRect.top + height;

        sublevelPosition.value = {
            top: targetRect.top - dropdownRect.top - 0.5,
            left: targetRect.right - dropdownRect.left,
            width: targetRect.width
        };

        sublevelMaxHeight.value = getViewportMaxHeight(
            window.innerHeight - targetRect.top - viewportMargin
        );

        borderRound.value =
            bottomSize >= targetRect.top + targetRect.height * list.length;

        isHover.value = true;
    } else {
        isHover.value = false;
        borderRound.value = true;
        sublevelMaxHeight.value = props.maxHeight;
    }
};

const keepSublevelVisible = () => {
    if (sublevel.value.length) {
        isHover.value = true;
    }
};

const handleMouseLeave = () => {
    isHover.value = false;
    borderRound.value = true;
    hoveredOption.value = null;
    sublevel.value = [];
    sublevelPosition.value = { top: 0, left: 0, width: 0 };
    sublevelMaxHeight.value = props.maxHeight;
};

onUnmounted(() => {
    stopObservingOptionList();
    endPersistentScrollbarDrag();
    if (persistentScrollbarAnimationFrame !== null) {
        window.cancelAnimationFrame(persistentScrollbarAnimationFrame);
    }
    document.removeEventListener('keydown', closeOnEscape);
    window.removeEventListener('resize', updateDropdownMaxHeight);
    document.removeEventListener('scroll', updateDropdownMaxHeight, true);
});
</script>

<template>
    <div class="dropdown relative focus:outline"
        :class="[disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer']" ref="dropdown"
        @mouseleave="handleMouseLeave"
        @focusout="!$event.currentTarget.contains($event.relatedTarget) && (open = false)">
        <button ref="trigger" :style="{ boxShadow: boxShadow || 'unset' }"
            class="relative grid h-full w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border border-principal rounded-t-input p-3 text-left text-xs font-medium tracking-wider transition-all duration-100 z-[4]"
            :class="[{ 'rounded-b-input': !open, '[&>svg]:-rotate-180': open }, `${background}`, principalClass]"
            tabindex="0" type="button" @click="open = !open" :disabled="disabled"
            :aria-expanded="open && !disabled" :aria-controls="optionsId">
            <div class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <span class="customDropDownLabel flex min-w-0 flex-1 items-center gap-2" :class="{ 'invert': invert }">
                    <div v-if="showColorSheet && getOptionColor(selectedOption)" class="header_dropdown_sheet_color"
                        :class="sheetColorClass"
                        :style="getOptionColorStyle(selectedOption)" />
                    <Icon v-if="selectedOption?.icon" :name="selectedOption.icon"
                        class="transition-all duration-300 shrink-0" />
                    <span
                        class="customDropDownLabelText block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left"
                        :class="classText" :title="getOptionTitle(selectedOption)"
                        @mouseenter="updateTruncatedTitle($event, selectedOption)">
                        {{ getOptionText(selectedOption) }}
                    </span>
                </span>
            </div>
            <div class="flex shrink-0 items-center justify-end gap-1 pl-2 overflow-visible"
                :class="clearable ? 'min-w-10' : 'min-w-4'">
                <span v-if="clearable" class="inline-flex h-4 w-4 shrink-0 items-center justify-center"
                    :class="canClearSelection ? 'cursor-pointer opacity-100' : 'pointer-events-none opacity-0'"
                    role="button" :aria-label="t('core.dropdown.clearSelection')" tabindex="-1" @mousedown.stop.prevent
                    @click.stop.prevent="clearSelection">
                    <Icon name="cross" :color="colorIcon" class="!w-4 !h-4" />
                </span>
                <div class="inline-flex h-4 w-4 shrink-0 items-center justify-center bg-transparent transition-transform transform duration-300"
                    :class="{ 'invert': invert, 'rotate-180': open }">
                    <Icon :name="triggerIcon" :color="colorIcon" class="!h-2 !w-3.5" />
                </div>
            </div>
        </button>

        <Transition appear enter-active-class="transition duration-300 transform"
            enter-from-class="opacity-0 -translate-y-4" enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-300 transform" leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4">
            <ul v-if="open && !disabled && options && selectedOption" :id="optionsId" ref="optionList"
                class="custom-dropdown-persistent-scrollbar absolute w-full border-x border-b border-principal rounded-b-input z-[1001] overflow-y-auto overflow-x-hidden"
                :class="[{ 'shadow-md': open }, `${background}`, {
                    'rounded-br-none': !borderRound || (sublevel.length <= 1 && isHover),
                    'custom-dropdown-has-overflow': persistentScrollbarVisible,
                }]"
                :style="{ maxHeight: dropdownMaxHeight }" @scroll.passive="queuePersistentScrollbarThumbPosition">
                <li v-for="option in options.filter(option => !isSameOption(option, selectedOption))"
                    :key="getOptionKey(option)" type="button" :class="firstBorder ? 'first:border-t' : ''"
                    class="border-b border-principal flex items-center gap-2 last:border-none px-3 h-[42px] hover:bg-principal/5 overflow-visible relative"
                    tabindex="0" @click="selectOption(option)" @keydown.enter.prevent="selectOption(option, $event)"
                    @keydown.space.prevent="selectOption(option, $event)" @mouseenter="handleItemHover(option, $event)"
                    @focus="handleItemHover(option, $event)">
                    <span class="customDropDownLabelChildren flex min-w-0 flex-1 items-center gap-2"
                        :class="{ 'invert': invert }">
                        <div v-if="showColorSheet && getOptionColor(option)" class="header_dropdown_sheet_color"
                            :class="sheetColorClass"
                            :style="getOptionColorStyle(option)" />
                        <Icon v-if="option?.icon" :name="option.icon"
                            class="transition-all duration-300 shrink-0" />
                        <span
                            class="customDropDownLabelChildrenText block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left"
                            :class="classTextChildren" :title="getOptionTitle(option)"
                            @mouseenter="updateTruncatedTitle($event, option)">
                            {{ getOptionText(option) }}
                        </span>
                    </span>
                    <Icon v-if="isGroupOption(option)" name="arrowShortRight" :color="colorIcon"
                        class="ml-auto transition-all duration-300 shrink-0" />
                </li>
            </ul>
        </Transition>
        <Transition appear enter-active-class="custom-dropdown-scrollbar-motion"
            enter-from-class="-translate-y-4" enter-to-class="translate-y-0"
            leave-active-class="custom-dropdown-scrollbar-motion" leave-from-class="translate-y-0"
            leave-to-class="-translate-y-4">
            <div v-if="open && !disabled" ref="persistentScrollbarRail"
                class="custom-dropdown-persistent-scrollbar-rail absolute right-px top-full z-[1002] w-2 select-none"
                :class="persistentScrollbarVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'"
                aria-hidden="true" @pointerdown="handlePersistentScrollbarRailPointerDown"
                @wheel.prevent.stop="handlePersistentScrollbarWheel">
                <span ref="persistentScrollbarThumb"
                    class="custom-dropdown-persistent-scrollbar-thumb absolute right-px top-0 block min-h-[18px] w-1.5 cursor-grab rounded-full will-change-transform active:cursor-grabbing"
                    @pointerdown="startPersistentScrollbarDrag" />
            </div>
        </Transition>
        <Transition enter-active-class="transition duration-300 transform" enter-from-class="opacity-0 -translate-x-2"
            enter-to-class="opacity-100 translate-x-0" leave-active-class="transition duration-300 transform"
            leave-from-class="opacity-100 translate-x-0" leave-to-class="opacity-0 -translate-x-2">
            <ul v-if="open && !disabled && isHover && sublevel.length" ref="sublevelList"
                class="absolute border-x border-principal rounded-r-input z-[1000] overflow-y-auto overflow-x-hidden"
                :class="[{ 'shadow-md': open || isHover }, `${background}`, { 'rounded-bl-input': !borderRound }]"
                :style="sublevelStyles" @mouseenter="keepSublevelVisible" @mouseleave="handleMouseLeave">
                <li v-for="item in sublevel" :key="item?.id ?? item?.value ?? item?.name ?? item" type="button"
                    class="border-b first:border-t border-principal flex items-center gap-2 px-3 h-[42.5px] last:h-[42px] hover:bg-principal/5 overflow-hidden"
                    tabindex="0" @click="selectOption(item)" @keydown.enter.prevent="selectOption(item, $event)"
                    @keydown.space.prevent="selectOption(item, $event)">
                    <span class="customDropDownLabelChildren flex min-w-0 flex-1 items-center gap-2"
                        :class="{ 'invert': invert }">
                        <span
                            class="customDropDownLabelChildrenText block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left"
                            :title="getOptionTitle(item)" @mouseenter="updateTruncatedTitle($event, item)">
                            {{ getOptionText(item) }}
                        </span>
                    </span>
                </li>
            </ul>
        </Transition>
    </div>
</template>

<style>
.dropdown ul {
    scrollbar-width: thin;
    scrollbar-color: var(--color-lineas) transparent;
}

.dropdown ul::-webkit-scrollbar {
    width: 8px;
    background: transparent;
}

.dropdown ul::-webkit-scrollbar-track,
.dropdown ul::-webkit-scrollbar-track-piece {
    background: transparent;
}

.dropdown ul::-webkit-scrollbar-thumb {
    background: var(--color-lineas);
    border-radius: 999px;
}

.dropdown ul::-webkit-scrollbar-button,
.dropdown ul::-webkit-scrollbar-button:start:decrement,
.dropdown ul::-webkit-scrollbar-button:end:increment,
.dropdown ul::-webkit-scrollbar-button:vertical:start:decrement,
.dropdown ul::-webkit-scrollbar-button:vertical:end:increment,
.dropdown ul::-webkit-scrollbar-corner,
.dropdown ul::-webkit-resizer {
    display: none;
    width: 0;
    height: 0;
    background: transparent;
}

.dropdown ul.custom-dropdown-persistent-scrollbar {
    overflow-anchor: none;
    overscroll-behavior: contain;
    scrollbar-width: none;
}

.dropdown ul.custom-dropdown-persistent-scrollbar.custom-dropdown-has-overflow {
    padding-right: 9px;
}

.dropdown ul.custom-dropdown-persistent-scrollbar::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
}

.custom-dropdown-persistent-scrollbar-thumb {
    background-color: var(--color-lineas, #878585);
}

.custom-dropdown-persistent-scrollbar-rail {
    transition: opacity 150ms ease;
}

.custom-dropdown-scrollbar-motion {
    transition: opacity 150ms ease, transform 300ms ease;
}
</style>
